import { useState, useMemo, memo, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  Slide,
  Avatar,
  Tooltip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TimelineIcon from "@mui/icons-material/Timeline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import FenceIcon from "@mui/icons-material/Fence";
import DevicesIcon from "@mui/icons-material/Devices";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FlagIcon from "@mui/icons-material/Flag";
import CircleIcon from "@mui/icons-material/Circle";
import MapIcon from "@mui/icons-material/Map";
import InventoryIcon from "@mui/icons-material/Inventory2";

/**
 * AssetTimeline Component
 * Displays a visual timeline/journey of an asset's events and locations
 */
const AssetTimeline = memo(function AssetTimeline({
  open,
  onClose,
  asset,
  events = [],
  facilities = {},
  onEventClick,
  onViewOnMap,
}) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const textPrimary = isDark ? "#ffffff" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0";
  const bgPaper = isDark ? "#032d42" : "#ffffff";
  const bgDefault = isDark ? "#021e2d" : "#f8fafc";

  const [selectedEventIndex, setSelectedEventIndex] = useState(null);

  // Event type configuration
  const eventConfig = {
    origin: { icon: <PlayArrowIcon />, color: navyColor, label: "Origin" },
    scan: { icon: <QrCodeScannerIcon />, color: greenColor, label: "Scanned" },
    arrival: { icon: <LocationOnIcon />, color: "#3b82f6", label: "Arrived" },
    departure: { icon: <LocalShippingIcon />, color: "#8b5cf6", label: "Departed" },
    entergeofence: { icon: <FenceIcon />, color: "#10b981", label: "Entered Zone" },
    leavegeofence: { icon: <FenceIcon />, color: "#f59e0b", label: "Left Zone" },
    moving: { icon: <LocalShippingIcon />, color: "#06b6d4", label: "In Transit" },
    assigned: { icon: <DevicesIcon />, color: greenColor, label: "Assigned" },
    unassigned: { icon: <DevicesIcon />, color: "#94a3b8", label: "Unassigned" },
    warehouse: { icon: <WarehouseIcon />, color: navyColor, label: "At Warehouse" },
    default: { icon: <CircleIcon />, color: textSecondary, label: "Event" },
  };

  // Get event config based on event name
  const getEventConfig = useCallback((eventName) => {
    if (!eventName) return eventConfig.default;
    const key = eventName.toLowerCase().replace(/\s+/g, "");
    return eventConfig[key] || eventConfig.default;
  }, [eventConfig, textSecondary]);

  // Process and sort events
  const timelineEvents = useMemo(() => {
    if (!events || events.length === 0) {
      // If no events provided, create mock timeline from asset data
      if (asset?.timeOfLogLong) {
        return [{
          id: `${asset.assetId}-current`,
          event: asset.lastEvent || "Current Location",
          timestamp: asset.timeOfLogLong,
          facilityId: asset.facilityId,
          latitude: asset.latitude,
          longitude: asset.longitude,
          isCurrent: true,
        }];
      }
      return [];
    }

    return events
      .sort((a, b) => (b.timestamp || b.timeOfLogLong || 0) - (a.timestamp || a.timeOfLogLong || 0))
      .map((event, index) => ({
        ...event,
        id: event.id || `event-${index}`,
        timestamp: event.timestamp || event.timeOfLogLong,
        isCurrent: index === 0,
        isFirst: index === events.length - 1,
      }));
  }, [events, asset]);

  // Format timestamp
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  // Format date for grouping
  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  }, []);

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups = {};
    timelineEvents.forEach((event) => {
      const dateKey = formatDate(event.timestamp);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    return groups;
  }, [timelineEvents, formatDate]);

  // Handle event click
  const handleEventClick = useCallback((event, index) => {
    setSelectedEventIndex(index);
    if (onEventClick) {
      onEventClick(event);
    }
  }, [onEventClick]);

  // Timeline event component
  const TimelineEvent = ({ event, index, isLast }) => {
    const config = getEventConfig(event.event || event.lastEvent);
    const facilityName = event.facilityId && facilities[event.facilityId]
      ? facilities[event.facilityId].name
      : null;
    const isSelected = selectedEventIndex === index;

    return (
      <Box
        onClick={() => handleEventClick(event, index)}
        sx={{
          display: "flex",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            "& .timeline-content": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
              transform: "translateX(4px)",
            },
          },
        }}
      >
        {/* Timeline line and dot */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "60px",
            flexShrink: 0,
          }}
        >
          {/* Dot/Icon */}
          <Tooltip title={config.label} arrow>
            <Avatar
              sx={{
                width: event.isCurrent ? "44px" : "36px",
                height: event.isCurrent ? "44px" : "36px",
                backgroundColor: event.isCurrent ? config.color : `${config.color}15`,
                border: `2px solid ${config.color}`,
                color: event.isCurrent ? "#ffffff" : config.color,
                boxShadow: event.isCurrent ? `0 0 0 4px ${config.color}25` : "none",
                transition: "all 0.2s ease",
                "& .MuiSvgIcon-root": {
                  fontSize: event.isCurrent ? "1.25rem" : "1rem",
                },
              }}
            >
              {config.icon}
            </Avatar>
          </Tooltip>

          {/* Connecting line */}
          {!isLast && (
            <Box
              sx={{
                width: "2px",
                flex: 1,
                minHeight: "40px",
                background: `linear-gradient(180deg, ${config.color}50 0%, ${borderColor} 100%)`,
                marginY: "8px",
              }}
            />
          )}
        </Box>

        {/* Event content */}
        <Box
          className="timeline-content"
          sx={{
            flex: 1,
            padding: "12px 16px",
            marginLeft: "8px",
            marginBottom: isLast ? 0 : "16px",
            borderRadius: "12px",
            backgroundColor: isSelected
              ? isDark
                ? `${config.color}15`
                : `${config.color}08`
              : "transparent",
            border: isSelected ? `1px solid ${config.color}30` : "1px solid transparent",
            transition: "all 0.2s ease",
          }}
        >
          {/* Event header */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: event.isCurrent ? "1rem" : "0.9375rem",
                  color: textPrimary,
                }}
              >
                {event.event || event.lastEvent || config.label}
              </Typography>
              {event.isCurrent && (
                <Chip
                  label="Current"
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    backgroundColor: `${greenColor}15`,
                    color: greenColor,
                    border: `1px solid ${greenColor}30`,
                  }}
                />
              )}
              {event.isFirst && !event.isCurrent && (
                <Chip
                  icon={<FlagIcon sx={{ fontSize: "0.75rem !important" }} />}
                  label="Start"
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    backgroundColor: `${navyColor}15`,
                    color: navyColor,
                    border: `1px solid ${navyColor}30`,
                    "& .MuiChip-icon": { color: navyColor },
                  }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: textSecondary,
              }}
            >
              {formatTime(event.timestamp)}
            </Typography>
          </Box>

          {/* Location */}
          {(facilityName || (event.latitude && event.longitude)) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: "0.875rem", color: textSecondary }} />
              <Typography sx={{ fontSize: "0.8125rem", color: textSecondary }}>
                {facilityName || `${event.latitude?.toFixed(4)}, ${event.longitude?.toFixed(4)}`}
              </Typography>
              {event.latitude && event.longitude && onViewOnMap && (
                <Chip
                  icon={<MapIcon sx={{ fontSize: "0.75rem !important" }} />}
                  label="View"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewOnMap(event);
                  }}
                  sx={{
                    height: "18px",
                    fontSize: "0.625rem",
                    ml: 1,
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    color: "#3b82f6",
                    border: `1px solid #3b82f640`,
                    "& .MuiChip-icon": { color: "#3b82f6" },
                    "&:hover": {
                      backgroundColor: "#3b82f610",
                    },
                  }}
                />
              )}
            </Box>
          )}

          {/* Additional details */}
          {event.description && (
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: textSecondary,
                mt: 1,
                lineHeight: 1.4,
              }}
            >
              {event.description}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
      PaperProps={{
        sx: {
          backgroundColor: bgPaper,
          borderRadius: "20px 20px 0 0",
          border: `1px solid ${borderColor}`,
          borderBottom: "none",
          boxShadow: isDark
            ? "0 -10px 40px rgba(0, 0, 0, 0.5)"
            : "0 -10px 40px rgba(0, 0, 0, 0.15)",
          maxHeight: "85vh",
          position: "fixed",
          bottom: 0,
          margin: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: "20px 24px",
          borderBottom: `1px solid ${borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          backgroundColor: bgPaper,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: isDark
                ? `linear-gradient(135deg, ${navyColor}40 0%, ${navyColor}20 100%)`
                : `linear-gradient(135deg, ${navyColor}15 0%, ${navyColor}05 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${navyColor}30`,
            }}
          >
            <TimelineIcon sx={{ color: navyColor, fontSize: "1.5rem" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: textPrimary,
              }}
            >
              Asset Journey
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InventoryIcon sx={{ fontSize: "0.875rem", color: greenColor }} />
              <Typography sx={{ fontSize: "0.875rem", color: textSecondary }}>
                {asset?.tag || "Unknown Asset"}
              </Typography>
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close timeline"
          sx={{
            backgroundColor: isDark ? "rgba(148, 163, 184, 0.1)" : "#f1f5f9",
            "&:hover": {
              backgroundColor: isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Summary stats */}
      <Box
        sx={{
          padding: "16px 24px",
          backgroundColor: bgDefault,
          display: "flex",
          gap: 3,
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "0.6875rem", color: textSecondary, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Total Events
          </Typography>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: navyColor }}>
            {timelineEvents.length}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.6875rem", color: textSecondary, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Current Status
          </Typography>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: greenColor }}>
            {timelineEvents[0]?.event || "Unknown"}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.6875rem", color: textSecondary, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Last Updated
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: textPrimary }}>
            {timelineEvents[0]?.timestamp ? formatTime(timelineEvents[0].timestamp) : "N/A"}
          </Typography>
        </Box>
      </Box>

      {/* Timeline content */}
      <Box
        sx={{
          padding: "24px",
          overflowY: "auto",
          maxHeight: "calc(85vh - 200px)",
        }}
      >
        {timelineEvents.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "40px 20px",
            }}
          >
            <TimelineIcon sx={{ fontSize: "3rem", color: textSecondary, opacity: 0.5, mb: 2 }} />
            <Typography sx={{ color: textPrimary, fontWeight: 500, mb: 0.5 }}>
              No Journey Data
            </Typography>
            <Typography sx={{ color: textSecondary, fontSize: "0.875rem" }}>
              Events will appear here as the asset is tracked
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedEvents).map(([date, dateEvents], groupIndex) => (
            <Box key={date} sx={{ mb: groupIndex < Object.keys(groupedEvents).length - 1 ? 3 : 0 }}>
              {/* Date header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: textSecondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {date}
                </Typography>
                <Box sx={{ flex: 1, height: "1px", backgroundColor: borderColor }} />
              </Box>

              {/* Events for this date */}
              {dateEvents.map((event, index) => (
                <TimelineEvent
                  key={event.id}
                  event={event}
                  index={timelineEvents.indexOf(event)}
                  isLast={index === dateEvents.length - 1 && groupIndex === Object.keys(groupedEvents).length - 1}
                />
              ))}
            </Box>
          ))
        )}
      </Box>
    </Dialog>
  );
});

export default AssetTimeline;
